import { FileStatus, isBulkInviteActive } from '@qse/api'
import type { BulkInviteRequest, BulkInviteRequestsResponse } from '@qse/api'
import {
  BulkInviteCompletedPayload,
  BulkInviteProgressPayload,
  CheckoutCompletedPayload,
  CheckoutFailedPayload,
  FileCopiedPayload,
  FileEncryptionCompletedPayload,
  FileEncryptionFailedPayload,
  FileEncryptionInProgressPayload,
  FileReadyForDownloadPayload,
  FolderUploadProgressPayload,
  ProfilePictureThumbnailsReadyPayload,
  QuotaAdjustmentPayload,
  QuotaScheduledForAdjustmentPayload,
  RsaKeysGenerationFailedPayload,
  RsaKeysReadyPayload,
  TenantSuspendedPayload,
  UserServiceUsageUpdatedPayload,
  UserStorageQuotaUpdatedPayload,
  UserSuspendedPayload,
  WebSocketEventType,
  WebSocketMessage,
} from '@qse/constants'
import { useActiveModelStore, useBillingStatusStore, useUserStore } from '@qse/stores'
import { toast } from '@qse/ui'
import { useGlobalFileDownload, useGlobalFileUpload } from '@qse/ui'
import { forceLogout, formatTruncated } from '@qse/utils'
import type { QueryClient } from '@tanstack/react-query'

import type { EventHandlers, RouteContext } from './types'

/**
 * Update file and folder queries based on route context using setQueryData
 * This is more efficient than invalidating as it updates the cache directly
 */
export function updateFileAndFolderQueries({
  queryClient,
  queryKeys,
  payload,
  routeContext,
  fileData,
  operation = 'update', // 'update', 'add', 'remove'
}: {
  queryClient: QueryClient
  queryKeys: typeof import('@qse/api').queryKeys
  payload: { id: string; parentId?: string }
  routeContext: RouteContext
  fileData?: any // Full file object from WebSocket
  operation?: 'update' | 'add' | 'remove'
}): void {
  // Check if we're in trash or favorites context - skip cache updates if so

  // if (shouldSkipCacheUpdatesWithLog('both', 'file and folder queries update')) {
  //   return
  // }

  // Update all file list queries that match the files.lists() pattern
  const filesListKey = queryKeys.files.lists()
  const queriesToUpdate = queryClient.getQueriesData({ queryKey: filesListKey })

  queriesToUpdate.forEach(([queryKey, cacheData]: [any, any]) => {
    if (!cacheData) return

    // Handle infinite queries (pagination)
    if (cacheData.pages && Array.isArray(cacheData.pages)) {
      const updatedPages = cacheData.pages.map((page: any) => {
        if (!page.data || !Array.isArray(page.data)) return page

        let updatedData = [...page.data]
        let countChange = 0

        if (operation === 'update' && fileData) {
          const fileIndex = updatedData.findIndex((f: any) => f.id === payload.id)

          if (fileIndex !== -1) {
            updatedData[fileIndex] = { ...updatedData[fileIndex], ...fileData }
          }
        } else if (operation === 'add' && fileData) {
          const fileExists = updatedData.some((f: any) => f.id === payload.id)

          if (!fileExists) {
            updatedData = [fileData, ...updatedData]
            countChange = 1
          }
        } else if (operation === 'remove') {
          const beforeLength = updatedData.length

          updatedData = updatedData.filter((f: any) => f.id !== payload.id)
          countChange = updatedData.length - beforeLength
        }

        return {
          ...page,
          data: updatedData,
          totalCount: Math.max(0, (page.totalCount || 0) + countChange),
        }
      })

      queryClient.setQueryData(queryKey, { ...cacheData, pages: updatedPages })
    }
    // Handle regular queries
    else if (cacheData.data && Array.isArray(cacheData.data)) {
      let updatedData = [...cacheData.data]

      if (operation === 'update' && fileData) {
        const fileIndex = updatedData.findIndex((f: any) => f.id === payload.id)

        if (fileIndex !== -1) {
          updatedData[fileIndex] = { ...updatedData[fileIndex], ...fileData }
        } else {
          return
        }
      } else if (operation === 'add' && fileData) {
        const fileExists = updatedData.some((f: any) => f.id === payload.id)

        if (!fileExists) {
          updatedData.unshift(fileData)
        }
      } else if (operation === 'remove') {
        updatedData = updatedData.filter((f: any) => f.id !== payload.id)
      }

      queryClient.setQueryData(queryKey, {
        ...cacheData,
        data: updatedData,
        totalCount:
          operation === 'add'
            ? cacheData.totalCount + 1
            : operation === 'remove'
              ? Math.max(0, cacheData.totalCount - 1)
              : cacheData.totalCount,
      })
    }
  })

  // Update file detail query for the specific file if we have data
  if (fileData && operation === 'update') {
    queryClient.setQueryData(queryKeys.files.detail(payload.id), fileData)
  } else if (operation === 'remove') {
    // Remove file detail from cache
    queryClient.removeQueries({ queryKey: queryKeys.files.detail(payload.id) })
  }

  // Invalidate folder details as they might need count updates
  // We can't reliably update folder counts without full folder data
  if (payload.parentId) {
    queryClient.invalidateQueries({
      queryKey: queryKeys.files.folderDetails(payload.parentId),
    })
  }
  if (routeContext.route === 'folder' && routeContext.parentId) {
    queryClient.invalidateQueries({
      queryKey: queryKeys.files.folderDetails(routeContext.parentId),
    })
  }
}

/**
 * Helper function to update ghost file status in cache
 */
function updateGhostFileStatus(
  fileId: string,
  status: 'AVAILABLE' | 'ENCRYPTING' | 'REMOVE',
  queryClient: QueryClient,
  queryKeys: typeof import('@qse/api').queryKeys,
) {
  const filesListKey = queryKeys.files.lists()
  const queriesToUpdate = queryClient.getQueriesData({ queryKey: filesListKey })

  queriesToUpdate.forEach(([queryKey, cacheData]: [any, any]) => {
    if (!cacheData) return

    // Handle infinite queries (pagination)
    if (cacheData.pages && Array.isArray(cacheData.pages)) {
      const updatedPages = cacheData.pages.map((page: any) => {
        if (!page.data || !Array.isArray(page.data)) return page

        const updatedData = page.data
          .map((item: any) => {
            if (item.id === fileId) {
              if (status === 'REMOVE') {
                return null // Mark for removal
              }

              return {
                ...item,
                status: status,
              }
            }

            return item
          })
          .filter(Boolean) // Remove null entries

        return {
          ...page,
          data: updatedData,
        }
      })

      queryClient.setQueryData(queryKey, { ...cacheData, pages: updatedPages })
    }
    // Handle regular queries
    else if (cacheData.data && Array.isArray(cacheData.data)) {
      const updatedData = cacheData.data
        .map((item: any) => {
          if (item.id === fileId) {
            if (status === 'REMOVE') {
              return null // Mark for removal
            }

            return {
              ...item,
              status: status,
            }
          }

          return item
        })
        .filter(Boolean) // Remove null entries

      queryClient.setQueryData(queryKey, {
        ...cacheData,
        data: updatedData,
      })
    }
  })
}

/**
 * Create event handlers for WebSocket messages
 */
export function createEventHandlers({
  queryClient,
  queryKeys,
  routeContext,
  eventHandlers,
}: {
  queryClient: QueryClient
  queryKeys: typeof import('@qse/api').queryKeys
  routeContext: RouteContext
  eventHandlers?: EventHandlers
}) {
  const { updateDriveQuota } = useUserStore.getState()

  return {
    [WebSocketEventType.FILE_ENCRYPTION_IN_PROGRESS]: (
      payload: FileEncryptionInProgressPayload,
    ) => {
      console.log('🔐 [FILE_ENCRYPTION_IN_PROGRESS] Received for file:', {
        fileId: payload.fileId,
        percentage: payload.progress.percentage,
        bytesProcessed: payload.progress.bytesProcessed,
        totalBytes: payload.progress.totalBytes,
        eta: payload.progress.estimatedTimeRemaining,
        timestamp: new Date().toISOString(),
      })

      // Update upload progress state with encryption progress
      const { updateFileEncryptionProgress } = useGlobalFileUpload.getState()

      updateFileEncryptionProgress(payload.fileId, {
        percentage: payload.progress.percentage,
        bytesProcessed: payload.progress.bytesProcessed,
        totalBytes: payload.progress.totalBytes,
        throughput: payload.progress.throughput,
        estimatedTimeRemaining: payload.progress.estimatedTimeRemaining,
      })

      eventHandlers?.onFileEncryptionInProgress?.(payload)
    },

    [WebSocketEventType.FILE_ENCRYPTION_COMPLETED]: (payload: FileEncryptionCompletedPayload) => {
      console.log('🔐 [FILE_ENCRYPTION_COMPLETED] Received for file:', {
        id: payload.file.id,
        name: payload.file.name,
        status: payload.file.status,
        timestamp: new Date().toISOString(),
      })

      // Update cache directly with the completed file data
      updateFileAndFolderQueries({
        queryClient,
        queryKeys,
        payload: payload.file,
        routeContext,
        fileData: payload.file,
        operation: 'update',
      })

      // Update upload progress state and ghost file in cache
      const { updateFileStatusFromWebSocket } = useGlobalFileUpload.getState()

      updateFileStatusFromWebSocket(payload.file.id, FileStatus.AVAILABLE, queryClient)

      console.log('File encryption completed:', payload)
      console.log('Current route context:', routeContext)

      eventHandlers?.onFileEncryptionCompleted?.(payload)
    },

    [WebSocketEventType.BILLING_PAST_DUE]: (payload: any) => {
      // Show warning for past due but don't force logout immediately
      toast.error(
        'Your subscription is past due. Please update your billing information to avoid service interruption.',
        {
          duration: 10000, // Show for 10 seconds
        },
      )

      eventHandlers?.onBillingPastDue?.(payload)
    },

    [WebSocketEventType.BILLING_UNPAID]: (payload: any) => {
      // Show clear message about unpaid status
      toast.error(
        'Your subscription is unpaid. You will be logged out and redirected to the login page.',
        {
          duration: 5000, // Show for 5 seconds
        },
      )

      // Add a small delay to ensure the toast is visible before logout
      setTimeout(() => {
        console.log('🚫 Billing unpaid - forcing logout...')
        forceLogout(true)
      }, 2000)

      eventHandlers?.onBillingUnpaid?.(payload)
    },

    [WebSocketEventType.BILLING_CANCELED]: (payload: any) => {
      // Show clear message about canceled subscription
      toast.error(
        'Your subscription has been canceled. You will be logged out and redirected to the login page.',
        {
          duration: 5000, // Show for 5 seconds
        },
      )

      // Add a small delay to ensure the toast is visible before logout
      setTimeout(() => {
        console.log('🚫 Billing canceled - forcing logout...')
        forceLogout(true)
      }, 2000)

      eventHandlers?.onBillingCanceled?.(payload)
    },

    [WebSocketEventType.FILE_ENCRYPTION_FAILED]: (payload: FileEncryptionFailedPayload) => {
      // Track encryption failure for folder uploads first
      const { updateFileEncryptionFailureForFolder } = useGlobalFileUpload.getState()

      updateFileEncryptionFailureForFolder(
        payload.file.id,
        payload.file.name,
        payload.file.path,
        payload.file.parentId,
      )

      // Check if this is part of an active folder upload
      const uploadState = useGlobalFileUpload.getState()
      const isPartOfActiveFolderUpload = uploadState.uploadingFiles.some(
        f =>
          f.isFolderUpload &&
          f.folderUploadId &&
          (payload.file.parentId === f.folderUploadId ||
            (f.pathToId && Array.from(f.pathToId.values()).includes(payload.file.parentId))),
      )

      if (isPartOfActiveFolderUpload) {
        // For folder uploads, just remove from UI (failures are tracked in folder progress)
        updateFileAndFolderQueries({
          queryClient,
          queryKeys,
          payload: {
            id: payload.file.id,
            parentId: payload.file.parentId,
          },
          routeContext,
          operation: 'remove',
        })
      } else {
        // For single files, mark as failed in upload tracker and remove from UI
        // Find the uploading file by serverFileId
        const uploadingFile = uploadState.uploadingFiles.find(
          f => f.serverFileId === payload.file.id,
        )

        if (uploadingFile) {
          // Mark as failed in upload tracker
          useGlobalFileUpload.setState({
            uploadingFiles: uploadState.uploadingFiles.map(f =>
              f.id === uploadingFile.id
                ? {
                    ...f,
                    status: FileStatus.FAILED,
                    errorMessage: `Encryption failed${payload.error ? `: ${payload.error}` : ''}`,
                  }
                : f,
            ),
          })
        }

        // Remove from UI
        updateFileAndFolderQueries({
          queryClient,
          queryKeys,
          payload: {
            id: payload.file.id,
            parentId: payload.file.parentId,
          },
          routeContext,
          operation: 'remove',
        })
      }

      console.error('File encryption failed, removed from UI:', payload)
      eventHandlers?.onFileEncryptionFailed?.(payload)
    },

    [WebSocketEventType.FILE_COPIED]: (payload: FileCopiedPayload) => {
      // Add the copied file to cache
      updateFileAndFolderQueries({
        queryClient,
        queryKeys,
        payload: payload.file,
        routeContext,
        fileData: payload.file,
        operation: 'add',
      })

      toast.success(`File "${payload.file.name}" copied successfully!`)
      console.log('File copied:', payload)
      console.log('Current route context:', routeContext)

      eventHandlers?.onFileCopied?.(payload)
    },

    [WebSocketEventType.FILE_READY_FOR_DOWNLOAD]: (payload: FileReadyForDownloadPayload) => {
      // Check if we have a URL and either download status is AVAILABLE or no download status (direct URL)
      const shouldDownload =
        payload.url && (!payload.download || payload.download.status === 'AVAILABLE')

      if (shouldDownload) {
        try {
          // check if it's preview not download
          const modalState = useActiveModelStore.getState()

          if (
            modalState.toggleModel &&
            modalState.modelActive === 'file-preview' &&
            modalState.data?.fileId === payload.file?.id
          ) {
            // Update modal data with the URL
            // Preserve NO_PREVIEW status if that was the original status (for unsupported files)
            const currentData = modalState.data
            const shouldPreserveNoPreview = currentData?.status === 'NO_PREVIEW'

            useActiveModelStore.setState({
              data: {
                ...currentData,
                url: payload.url,
                status: shouldPreserveNoPreview ? 'NO_PREVIEW' : 'AVAILABLE',
              },
            })

            return
          }

          // Let the download tracker handle the auto-download via url
          const { markAvailable } = useGlobalFileDownload.getState()
          const id = payload.file?.id || payload.url

          markAvailable(id, payload.url)
        } catch {
          const modalState = useActiveModelStore.getState()

          if (
            modalState.toggleModel &&
            modalState.modelActive === 'file-preview' &&
            modalState.data?.fileId === payload.file?.id
          ) {
            // Preserve NO_PREVIEW status if that was the original status (for unsupported files)
            const currentData = modalState.data
            const shouldPreserveNoPreview = currentData?.status === 'NO_PREVIEW'

            useActiveModelStore.setState({
              data: {
                ...currentData,
                url: payload.url,
                status: shouldPreserveNoPreview ? 'NO_PREVIEW' : 'AVAILABLE',
              },
            })

            return
          }
          // fallback: trigger browser download
          const link = document.createElement('a')

          link.href = payload.url
          link.download = payload.file?.name || 'download'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      }

      console.log('File ready for download:', payload)
      eventHandlers?.onFileReadyForDownload?.(payload)
    },

    [WebSocketEventType.FILE_DOWNLOAD_REQUEST_FAILED]: (payload: any) => {
      try {
        const modalState = useActiveModelStore.getState()

        if (
          modalState.toggleModel &&
          modalState.modelActive === 'file-preview' &&
          modalState.data?.fileId === payload.file?.id
        ) {
          useActiveModelStore.setState({
            data: {
              ...modalState.data,
              status: 'FAILED',
              errorMessage: payload.error || 'Download failed',
            },
          })

          return
        }

        const { markFailed } = useGlobalFileDownload.getState()

        markFailed(payload.file.id, payload.error)
      } catch (error) {
        console.error('Failed to mark file download request failed:', error)
      }
      console.error('File download request failed:', payload)
      eventHandlers?.onFileDownloadRequestFailed?.(payload)
    },

    [WebSocketEventType.FILE_COPY_FAILED]: (payload: any) => {
      toast.error('File copy failed.')
      console.error('File copy failed:', payload)
      eventHandlers?.onFileCopyFailed?.(payload)
    },

    [WebSocketEventType.FOLDER_UPLOAD_PROGRESS]: (payload: FolderUploadProgressPayload) => {
      // Update upload progress state with real-time data and ghost folder in cache
      const { updateFolderUploadProgressFromWebSocket } = useGlobalFileUpload.getState()

      updateFolderUploadProgressFromWebSocket(payload, queryClient)

      // Process individual file status updates from the new file ID arrays
      const encryptionProgress = payload.progress.encryption

      // Update files that completed encryption to AVAILABLE
      if (encryptionProgress.completedFileIds && encryptionProgress.completedFileIds.length > 0) {
        encryptionProgress.completedFileIds.forEach(fileId => {
          updateGhostFileStatus(fileId, 'AVAILABLE', queryClient, queryKeys)
        })
      }

      // Update files currently encrypting to ENCRYPTING
      // BUT: Don't override files that have already been marked as FAILED
      if (encryptionProgress.encryptingFileIds && encryptionProgress.encryptingFileIds.length > 0) {
        const uploadState = useGlobalFileUpload.getState()

        encryptionProgress.encryptingFileIds.forEach(fileId => {
          // Check if this file was already marked as FAILED in the upload tracker
          const failedFile = uploadState.uploadingFiles.find(
            f => f.serverFileId === fileId && f.status === FileStatus.FAILED,
          )

          // Only update to ENCRYPTING if the file hasn't been marked as FAILED
          if (!failedFile) {
            updateGhostFileStatus(fileId, 'ENCRYPTING', queryClient, queryKeys)
          }
        })
      }

      // Remove files that failed encryption
      if (encryptionProgress.failedFileIds && encryptionProgress.failedFileIds.length > 0) {
        encryptionProgress.failedFileIds.forEach(fileId => {
          updateGhostFileStatus(fileId, 'REMOVE', queryClient, queryKeys)
        })
      }

      // Remove files that were permanently deleted
      if (
        payload.progress.permanentlyDeletedFileIds &&
        payload.progress.permanentlyDeletedFileIds.length > 0
      ) {
        payload.progress.permanentlyDeletedFileIds.forEach(fileId => {
          updateGhostFileStatus(fileId, 'REMOVE', queryClient, queryKeys)
        })
      }

      // Get current folder from tracker to access frontend-tracked failures
      const currentFolder = useGlobalFileUpload
        .getState()
        .uploadingFiles.find(f => f.isFolderUpload && f.folderUploadId === payload.folderId)

      const filesListKey = queryKeys.files.lists()
      const queriesToUpdate = queryClient.getQueriesData({ queryKey: filesListKey })

      // Determine folder status based on progress
      const uploadProgress = payload.progress.upload
      const totalFiles = payload.totalFiles

      // Merge backend failures with frontend-tracked failures
      // Frontend tracks files that failed locally (deleted from user's computer, etc.)
      const frontendFailedFiles = currentFolder?.failedUploads || 0

      // Calculate completion accounting for ALL failed files (frontend + backend)
      // Upload phase complete when: uploaded + all_failures >= totalFiles
      const uploadCompleted =
        (currentFolder?.uploadedFiles || 0) + frontendFailedFiles >= totalFiles

      // Encryption phase complete when: encrypted + encryption_failed >= successfully_uploaded_files
      // (We only encrypt files that uploaded successfully, not the ones that failed during upload)
      const successfullyUploadedFiles = uploadProgress.completed
      const encryptionCompleted =
        encryptionProgress.total + (payload.progress.permanentlyDeletedFileIds?.length || 0) >=
        (currentFolder?.uploadedFiles || 0) + frontendFailedFiles

      // ALWAYS calculate status based on progress numbers
      // Don't trust backend's generic statuses like "IN_PROGRESS" - calculate from actual progress
      let folderStatus: string

      // Mark as AVAILABLE when both phases are complete (accounting for failures)
      // Or if no files uploaded successfully (all failed during upload)
      if (uploadCompleted && (encryptionCompleted || successfullyUploadedFiles === 0)) {
        folderStatus = 'AVAILABLE'
      } else if (uploadCompleted) {
        // Upload complete, now encrypting the successfully uploaded files
        folderStatus = 'ENCRYPTING'
      } else {
        // Still uploading - prefer showing this even if some encryption started
        folderStatus = 'UPLOADING'
      }

      console.log('📊 [FOLDER_UPLOAD_PROGRESS] Status calculation:', {
        payloadStatus: payload.status,
        totalFiles,
        frontendFailed: frontendFailedFiles,
        uploadCompleted,
        encryptionCompleted,
        calculatedStatus: folderStatus,
        uploadProgress: `${currentFolder?.uploadedFiles}/${totalFiles} (${frontendFailedFiles} failed)`,
        encryptionProgress: `${encryptionProgress.completed}/${successfullyUploadedFiles} (${encryptionProgress.failed} failed)`,
        completedFileIds: encryptionProgress.completedFileIds?.length || 0,
        encryptingFileIds: encryptionProgress.encryptingFileIds?.length || 0,
        failedFileIds: encryptionProgress.failedFileIds?.length || 0,
        permanentlyDeletedFileIds: payload.progress.permanentlyDeletedFileIds?.length || 0,
      })

      let updatedCount = 0

      queriesToUpdate.forEach(([queryKey, cacheData]: [any, any]) => {
        if (!cacheData) return

        // Handle infinite queries (pagination)
        if (cacheData.pages && Array.isArray(cacheData.pages)) {
          const updatedPages = cacheData.pages.map((page: any) => {
            if (!page.data || !Array.isArray(page.data)) return page

            const updatedData = page.data.map((item: any) => {
              if (item.id === payload.folderId) {
                updatedCount++

                return {
                  ...item,
                  status: folderStatus,
                }
              }

              return item
            })

            return {
              ...page,
              data: updatedData,
            }
          })

          queryClient.setQueryData(queryKey, { ...cacheData, pages: updatedPages })
        }
        // Handle regular queries
        else if (cacheData.data && Array.isArray(cacheData.data)) {
          const updatedData = cacheData.data.map((item: any) => {
            if (item.id === payload.folderId) {
              updatedCount++

              return {
                ...item,
                status: folderStatus,
              }
            }

            return item
          })

          queryClient.setQueryData(queryKey, {
            ...cacheData,
            data: updatedData,
          })
        }
      })

      // Check if the user's current path contains the folderId from the payload
      // Invalidate folder details to update file counts
      if (routeContext.parentId === payload.folderId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.files.folderDetails(payload.folderId),
        })
      }

      eventHandlers?.onFolderUploadProgress?.(payload)
    },

    [WebSocketEventType.USER_STORAGE_QUOTA_UPDATED]: (payload: UserStorageQuotaUpdatedPayload) => {
      // Update Zustand useUserStore with new storage quota values
      if (payload.quota) {
        const updatedDrive = {
          status: 'READY' as const,
          quotaSummary: payload.quota,
        }

        updateDriveQuota(updatedDrive)
      }

      eventHandlers?.onUserStorageQuotaUpdated?.(payload)
    },

    [WebSocketEventType.USER_SERVICE_USAGE_UPDATED]: (payload: UserServiceUsageUpdatedPayload) => {
      // Update Zustand useUserStore with new service quota values
      if (payload.quota) {
        const updatedDrive = {
          status: 'READY' as const,
          quotaSummary: payload.quota,
        }

        updateDriveQuota(updatedDrive)
        console.log('Service usage updated:', payload)
      }

      eventHandlers?.onUserServiceUsageUpdated?.(payload)
    },

    [WebSocketEventType.CHECKOUT_FAILED]: (payload: CheckoutFailedPayload) => {
      eventHandlers?.onCheckoutFailed?.(payload)
    },

    [WebSocketEventType.CHECKOUT_COMPLETED]: (payload: CheckoutCompletedPayload) => {
      eventHandlers?.onCheckoutCompleted?.(payload)
    },

    [WebSocketEventType.USER_SUSPENDED]: (payload: UserSuspendedPayload) => {
      console.log('🚫 User suspended, logging out...', payload)

      // Show suspension message with more details
      toast.error(
        'Your account has been suspended. You will be logged out and redirected to the login page.',
        {
          duration: 5000, // Show for 5 seconds
        },
      )

      // Call custom handler first if provided
      if (eventHandlers?.onUserSuspended) {
        eventHandlers.onUserSuspended(payload)
      } else {
        // Default behavior: force logout with delay to ensure toast visibility |
        setTimeout(() => {
          console.log('🚫 User suspended - forcing logout...')
          forceLogout(true)
        }, 2000)
      }
    },

    [WebSocketEventType.TENANT_SUSPENDED]: (payload: TenantSuspendedPayload) => {
      // Show suspension message with company name and custom message
      toast.error(`Organization "${payload.companyName}" has been suspended. ${payload.message}`)

      // Call custom handler first if provided
      if (eventHandlers?.onTenantSuspended) {
        eventHandlers.onTenantSuspended(payload)
      } else {
        // Default behavior: force logout with delay to ensure toast visibility
        setTimeout(() => {
          forceLogout(true)
        }, 3000) // Slightly longer delay to allow reading the message
      }
    },

    [WebSocketEventType.RSA_KEYS_READY]: (payload: RsaKeysReadyPayload) => {
      console.log('🔑 RSA keys generation completed')
      toast.success('RSA keys generated successfully!')

      eventHandlers?.onRsaKeysReady?.(payload)
    },

    [WebSocketEventType.RSA_KEYS_GENERATION_FAILED]: (payload: RsaKeysGenerationFailedPayload) => {
      console.error('❌ RSA keys generation failed:', payload)
      toast.error(`RSA keys generation failed${payload.error ? `: ${payload.error}` : ''}`)

      eventHandlers?.onRsaKeysGenerationFailed?.(payload)
    },

    [WebSocketEventType.PROFILE_PICTURE_THUMBNAILS_READY]: (
      payload: ProfilePictureThumbnailsReadyPayload,
    ) => {
      console.log('Profile picture thumbnails ready:', payload)

      // Invalidate profile picture queries to refetch with new thumbnail URLs
      // Success toast will be shown by useProfilePictureManager hook when this query succeeds
      queryClient.invalidateQueries({
        queryKey: queryKeys.profilePicture.current(),
      })

      // Also invalidate user data to update any cached profile picture info
      queryClient.invalidateQueries({
        queryKey: queryKeys.auth.me(),
      })

      eventHandlers?.onProfilePictureThumbnailsReady?.(payload)
    },

    [WebSocketEventType.QUOTA_ADJUSTMENT]: (payload: QuotaAdjustmentPayload) => {
      console.log('📉 Quota adjustment:', payload)

      const message = `Your quota was reduced from ${formatTruncated(payload.previousQuotaGb)}GB to ${formatTruncated(payload.newQuotaGb)}GB due to your organization's plan downgrade`

      toast.warning(message, {
        duration: 10000, // Show for 10 seconds
      })
    },

    [WebSocketEventType.QUOTA_SCHEDULED_FOR_ADJUSTMENT]: (
      payload: QuotaScheduledForAdjustmentPayload,
    ) => {
      console.log('📅 Quota scheduled for adjustment:', payload)

      // Update store with quota adjustment grace period status
      // This will trigger the banner display for tenant admins
      const { setQuotaAdjustmentGracePeriodStatus, setShowQuotaAdjustmentBanner } =
        useBillingStatusStore.getState()

      setQuotaAdjustmentGracePeriodStatus({
        hasActiveGracePeriod: true,
        targetPlan: payload.targetPlan,
      })

      setShowQuotaAdjustmentBanner(true)
    },

    [WebSocketEventType.BULK_INVITE_PROGRESS]: (payload: BulkInviteProgressPayload) => {
      console.log('📨 [BULK_INVITE_PROGRESS] Received:', payload)

      const patch = {
        status: payload.status,
        successCount: payload.successCount,
        failureCount: payload.failureCount,
        skippedCount: payload.skippedCount,
      }

      // Patch the caches in place — invalidating here would refetch on every
      // frame, recreating the polling these events replace. Terminal rows are
      // never regressed: a stale frame can land after BULK_INVITE_COMPLETED.
      queryClient.setQueryData<BulkInviteRequest>(
        queryKeys.bulkInvitations.detail(payload.requestId),
        old => (old && isBulkInviteActive(old.status) ? { ...old, ...patch } : old),
      )
      queryClient.setQueriesData<BulkInviteRequestsResponse>(
        { queryKey: queryKeys.bulkInvitations.lists() },
        old => {
          if (!old?.data) return old

          let changed = false
          const data = old.data.map(request => {
            if (request.id !== payload.requestId || !isBulkInviteActive(request.status)) {
              return request
            }

            changed = true

            return { ...request, ...patch }
          })

          return changed ? { ...old, data } : old
        },
      )

      eventHandlers?.onBulkInviteProgress?.(payload)
    },

    [WebSocketEventType.BULK_INVITE_COMPLETED]: (payload: BulkInviteCompletedPayload) => {
      console.log('📨 [BULK_INVITE_COMPLETED] Received:', payload)

      // Refresh the affected request and the history list so the UI reflects the
      // terminal status without waiting for the next poll. App-level handlers
      // (e.g. admin toast) run afterwards.
      queryClient.invalidateQueries({
        queryKey: queryKeys.bulkInvitations.detail(payload.requestId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.bulkInvitations.lists(),
      })

      eventHandlers?.onBulkInviteCompleted?.(payload)
    },
  }
}

/**
 * Process WebSocket message and dispatch to appropriate handler
 */
export function processWebSocketMessage(
  message: WebSocketMessage,
  handlers: ReturnType<typeof createEventHandlers>,
): void {
  const handler = handlers[message.event as WebSocketEventType]

  if (handler) {
    handler(message.data)
  } else {
    console.warn('Unknown WebSocket event:', message.event)
  }
}
